/** @format */

import { CustomTitle } from '@/components/page/PageRoom/index.style';
import { utilityGtmMoreOption } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import type { TRoom } from '@/ducks/room/declarations';
import { selectorAvailabilityArray } from '@/ducks/shared/selectors';
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import { useAppSelector } from '@/redux/hooks';
import { utilityFilterAvailabilityArray } from '@/utils/helpers/utilityFilterAvailabilityArray';
import { FormattedMessage } from 'gatsby-plugin-intl';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CardRoomSkeleton } from '../page/PageListing/components/CardRoomSkeleton';
import { utilityGTMListingViewItemList } from '@/utils/helpers/utilityGTMListingViewItemList';
import { selectorFilters } from '@/ducks/listing/selectors';
import snakeCase from 'lodash/snakeCase';
import { apiRoomAlternatives } from '@/api/apiRoomAlternative';
import type { TLocale } from '../../utils/gatsby/types';
import isEmpty from 'lodash/isEmpty';
import { StringParam, useQueryParam } from 'use-query-params';
import { NAME_PORTAL_CODE } from '../../utils/gatsby/constants';

const CardRoom = lazy(() => import('../page/PageListing/components/CardRoom'));

type AlternativesFilter = 'Building' | 'Neighborhood' | 'Price';

interface Props {
  language: TLocale;
  checkInDate: string;
  room: TRoom;
}

const controller = new AbortController();

const MoreOptions = ({ language, checkInDate, room }: Props) => {
  const [portalCode = NAME_PORTAL_CODE] = useQueryParam('portalCode', StringParam);

  const { code, building } = room;
  const moreOptionsRef = React.useRef(null);
  const [gtmStart, setGtmStart] = useState(false);
  const { t, intl } = useAppTranslation();
  const availabilityArray = useAppSelector(selectorAvailabilityArray);
  const filters = useAppSelector(selectorFilters);

  const [currentAlternativeFilter, setCurrentAlternativeFilter] = useState<AlternativesFilter[number] | null>(null);

  const [alternativesList, setAlternativesList] = useState<Record<AlternativesFilter, any[]>>({
    Building: [],
    Neighborhood: [],
    Price: [],
  });

  const filteredAlternatives = useMemo(() => {
    return Object.keys(alternativesList).reduce(
      (acc, key) => {
        acc[key as AlternativesFilter] =
          alternativesList[key as AlternativesFilter].length > 0 ? alternativesList[key as AlternativesFilter] : [];
        return acc;
      },
      { Building: [], Neighborhood: [], Price: [] } as typeof alternativesList,
    );
  }, [alternativesList]);

  const showMoreOptionComponent = useMemo(() => {
    return Object.values(alternativesList).some(option => option.length > 0);
  }, [alternativesList]);

  useEffect(() => {
    const loadAllAlternatives = async () => {
      if (!code) return;

      const searchByOptions = ['Building', 'Neighborhood', 'Price'] as const;
      const responses = await Promise.all(
        searchByOptions.map(option =>
          apiRoomAlternatives({
            bedCode: code,
            checkInDate,
            buildingId: building.id,
            bedAlternativesSearchBy: option,
            language: language,
            portalCode,
            controller,
          }).catch(() => ({ items: [] })),
        ),
      );

      const [buildingResponse, neighborhoodResponse, priceResponse] = responses;

      const alternativesList = {
        Building: buildingResponse.items.slice(0, 2),
        Neighborhood: neighborhoodResponse.items.slice(0, 2),
        Price: priceResponse.items.slice(0, 2),
      };

      const firstWithItems =
        (['Building', 'Neighborhood', 'Price'] as const).find(option => alternativesList[option].length > 0) || null;

      if (firstWithItems) {
        setCurrentAlternativeFilter(firstWithItems);
      }

      setAlternativesList(alternativesList);
    };

    loadAllAlternatives();
  }, [code]);

  const renderCardRoom = (item: TRoom, idx: number) => {
    return (
      <Suspense fallback={<CardRoomSkeleton />}>
        <CardRoom key={item.code} room={item} index={idx + 1} />
      </Suspense>
    );
  };

  const setGTMMoreOption = (
    choice: 'filter' | 'listing_room',
    selectedOption: AlternativesFilter | string,
    roomRef: string,
  ) => {
    const changeSelectedName = (selectedOption: AlternativesFilter | string) => {
      switch (selectedOption) {
        case 'Building':
          return t('pdp.more_options.same_building' as TTranslation);
        case 'Neighborhood':
          return t('pdp.more_options.same_area' as TTranslation);
        case 'Price':
          return t('pdp.more_options.same_price' as TTranslation);
        default:
          return selectedOption;
      }
    };

    const selection = changeSelectedName(selectedOption);
    utilityGtmMoreOption(choice, snakeCase(selection), roomRef);
  };

  const onSelectTab = (selectedTab: AlternativesFilter) => {
    setGtmStart(true);
    setCurrentAlternativeFilter(selectedTab);
  };

  useEffect(() => {
    if (currentAlternativeFilter === null) return;
    if (gtmStart) setGTMMoreOption('filter', currentAlternativeFilter, code);
  }, [currentAlternativeFilter]);

  useEffect(() => {
    if (currentAlternativeFilter === null) return;

    const selectedAlternatives = alternativesList[currentAlternativeFilter as AlternativesFilter];

    const filteredAvailabilityArray = utilityFilterAvailabilityArray(
      availabilityArray,
      currentAlternativeFilter ? alternativesList[currentAlternativeFilter as AlternativesFilter] : [],
    );

    if (selectedAlternatives.length === filteredAvailabilityArray.length) {
      utilityGTMListingViewItemList(selectedAlternatives, filteredAvailabilityArray, intl, filters, 'related_products');
    }
  }, [alternativesList, currentAlternativeFilter, availabilityArray]);

  const handleClick = (room: TRoom) => {
    setGTMMoreOption('listing_room', room.code, code);
  };

  if (!showMoreOptionComponent) return null;

  return (
    <MoreOptionsSection ref={moreOptionsRef}>
      <>
        <CustomTitle $subtitle>
          <FormattedMessage id="pdp.more_options.title" />
        </CustomTitle>
        <MoreOtionsButtonContainer>
          <ul>
            {!isEmpty(filteredAlternatives['Building']) && (
              <li>
                <CustomButton
                  $selected={currentAlternativeFilter === 'Building'}
                  onClick={() => onSelectTab('Building')}
                  type="button"
                >
                  <FormattedMessage id="pdp.more_options.same_building" />
                </CustomButton>
              </li>
            )}
            {!isEmpty(filteredAlternatives['Neighborhood']) && (
              <li>
                <CustomButton
                  $selected={currentAlternativeFilter === 'Neighborhood'}
                  onClick={() => onSelectTab('Neighborhood')}
                  type="button"
                >
                  <FormattedMessage id="pdp.more_options.same_area" />
                </CustomButton>
              </li>
            )}
            {!isEmpty(filteredAlternatives['Price']) && (
              <li>
                <CustomButton
                  $selected={currentAlternativeFilter === 'Price'}
                  onClick={() => onSelectTab('Price')}
                  type="button"
                >
                  <FormattedMessage id="pdp.more_options.same_price" />
                </CustomButton>
              </li>
            )}
          </ul>
        </MoreOtionsButtonContainer>

        {/* Showing only two alternatives at most, regardless of what the backend is sending */}
        {/* because otherwise the UI is ugly as f */}
        {currentAlternativeFilter !== null &&
          filteredAlternatives[currentAlternativeFilter as AlternativesFilter].length > 0 && (
            <MoreOptionsCardContainer>
              {filteredAlternatives[currentAlternativeFilter as AlternativesFilter].map(
                (room: TRoom, index: number) => (
                  <div onClick={() => handleClick(room)} aria-hidden="true" key={room.code}>
                    {renderCardRoom(room, index)}
                  </div>
                ),
              )}
            </MoreOptionsCardContainer>
          )}
      </>
    </MoreOptionsSection>
  );
};

const MoreOptionsSection = styled.section`
  position: relative;
`;

const MoreOptionsCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  div[aria-hidden='true'] {
    max-width: 100%;
  }

  &.error {
    font-family: ${({ theme }) => theme.font.body};
    font-weight: bold;
    font-size: 24px;
    line-height: 1.2;
    margin: 0 !important;

    div {
      width: 100%;
      padding-bottom: 20px;
    }
  }
`;

const CustomButton = styled.button<{ $selected: boolean }>`
  --bg-color: ${({ theme, $selected }) => ($selected ? theme.colors.joivy['teal-dark'] : theme.colors.joivy.white)};
  --border-color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  --color: ${({ theme, $selected }) => ($selected ? theme.colors.joivy.white : theme.colors.joivy['teal-dark'])};

  color: var(--color);
  text-decoration: none;
  font-size: 0.9375rem;
  cursor: pointer;
  padding: 0.75rem 0.9375rem;
  background-color: var(--bg-color);
  border: 1px solid var(--border-color);
  transition: background-color 0.3s ease 0s, border 0.3s ease 0s;
  border-radius: 0.625rem;

  &:hover,
  &:active,
  &:focus {
    --bg-color: ${({ theme }) => theme.colors.joivy['teal-dark']};
    --color: ${({ theme }) => theme.colors.joivy.white};
  }
`;

const MoreOtionsButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  overflow: hidden;

  > a {
    color: ${({ theme }) => theme.colors.joivy.black} !important;
    display: inline-block;
    order: 0;

    @media screen and (min-width: ${props => props.theme.breakpoints.xs}) {
      order: 1;
    }
  }

  ul {
    padding: 0;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    overflow-x: auto;
    gap: 0 10px;
    list-style: none;
    margin: 0;
    padding-bottom: 1.25rem;

    li {
      cursor: pointer;
      color: ${({ theme }) => theme.colors.joivy.black};
      white-space: nowrap;
    }
  }
`;

export default MoreOptions;
